import { availableDispositions, availableOfferTypes, PropertyTypesValues } from "values";
import { AnyFilterTypes, FilterTypes, FlatsFilter, OfferType } from "Types";
import routes from "routes";
import { initialState } from "ContextStore";
import { regions } from "Components/Filters/Map";

export const getPathFromFilter = (filter: AnyFilterTypes): string => {
    const parts = [];

    let propertyAndOfferTypes: string | null;
    // handle discounted offers
    if ("isOther" in filter) {
        propertyAndOfferTypes = routes.other;
    }

    // handle other types
    else {
        // TypeOfProperty + OfferType: eg: [byty|domy|...]-[prodej|pronajem|prodej-pronajem]
        propertyAndOfferTypes = filter.typeOfProperty
            ? PropertyTypesValues[filter.typeOfProperty].path
            : routes.commercial;
    }
    if (filter.offerType !== null) {
        const offerTypeObjects = availableOfferTypes
            .filter((item) => item.urlValue !== null && (filter.offerType === item.id || filter.offerType === 0))
            .map((item) => item.urlValue);
        if (offerTypeObjects) {
            propertyAndOfferTypes = `${propertyAndOfferTypes}-${offerTypeObjects.join("-")}`;
        }
    }
    parts.push(propertyAndOfferTypes);

    // dispositions
    // todo add IE polyfil
    if ("dispositions" in filter && filter.dispositions && filter.dispositions.length > 0) {
        const cleanedDispositions = filter.dispositions
            .filter((d) => d !== null && d !== undefined)
            .map((d) => d.urlValue);
        if (cleanedDispositions.length > 0) {
            parts.push(cleanedDispositions.join("-"));
        }
    }

    // price from and to
    if (filter.priceFrom || filter.priceTo) {
        const { priceFrom, priceTo } = filter;
        const prices = [];
        if (priceFrom) {
            prices.push(`cena-od-${priceFrom}`);
        }
        if (priceFrom && priceTo) {
            prices.push(`do-${priceTo}`);
        } else if (priceTo) {
            prices.push(`cena-do-${priceTo}`);
        }

        parts.push(prices.join("-"));
    }

    // regions
    if (filter.regions) {
        parts.push(filter.regions.map((region) => region.id).join("+"));
    }

    const joinedPats = parts.filter((part) => part != "").join("/");
    return `/${joinedPats}`;
};

const typeRegex = new RegExp(
    /(byty|domy|pozemky|komercni-nemovitosti|ostatni)-?(prodej-pronajem|prodej|pronajem)?/,
    "i"
);
const dispositionRegex = new RegExp(/(\d\+[1,k]{1,2}|jiny|atypicky)/, "gi");
const priceRegex = /cena-(od-(?<priceFrom>\d+))?-?(do-(?<priceTo>\d+))?/i;
const regionRegex = new RegExp(/([a-z-])+/, "gi");

const getFilterType = (propertyType: string): FilterTypes | null => {
    switch (propertyType) {
        case "byty":
            return FilterTypes.flats;
        case "domy":
            return FilterTypes.houses;
        case "pozemky":
            return FilterTypes.lands;
        case "komercni-nemovitosti":
            return FilterTypes.commercials;
        case "ostatni":
            return FilterTypes.others;
    }
    return null;
};
export const getInitialFilter = (propertyType: string): AnyFilterTypes | null => {
    const type = getFilterType(propertyType);
    return type ? { ...initialState.filter[type] } : null;
};

export const getFilterFromPath = (
    path: string
): {
    type: FilterTypes | null;
    filter: AnyFilterTypes;
} | null => {
    const parts = path.split("/");
    // part[0] is empty, because path starts with /
    parts.shift();
    // if it is empty now, now filter can be created
    if (parts.length === 0 || !parts[0]) {
        return null;
    }

    // first part with property type and offer type is required and always present
    const types = parts.shift() as string; //we now it can't be undefined now

    // typeOfProperty-offerType
    const typesParts = types.match(typeRegex);
    if (!typesParts || typesParts.length === 0) {
        return null;
    }
    const availableOfferType = availableOfferTypes.find((type) => type.urlValue === typesParts[2])?.id;

    // create filter
    const filter = getInitialFilter(typesParts[1]);

    if (!filter) {
        console.warn(`Unknown property type ${typesParts[1]}`);
        return null;
    }
    // set offer type
    filter.offerType = availableOfferType ? availableOfferType : OfferType.all;

    // set all other filter parts
    parts.forEach((part) => {
        if (dispositionRegex.test(part)) {
            const matches = part.match(dispositionRegex);
            if (matches) {
                const dispositions = availableDispositions.filter((disp) => matches.includes(disp.urlValue));
                (filter as FlatsFilter).dispositions = [];
                dispositions.forEach((disp) => ((filter as FlatsFilter).dispositions![disp.id] = disp));
            }
        } else if (priceRegex.test(part)) {
            const matches = priceRegex.exec(part);
            if (matches?.groups?.priceFrom) {
                filter.priceFrom = parseInt(matches.groups.priceFrom);
            }
            if (matches?.groups?.priceTo) {
                filter.priceTo = parseInt(matches.groups.priceTo);
            }
        } else if (regionRegex.test(part)) {
            const matches = part.match(regionRegex);
            if (matches) {
                filter.regions = regions
                    .filter((region) => matches.includes(region.id))
                    .map((region) => ({ id: region.id, label: region.label }));
            }
        }
    });

    return {
        type: getFilterType(typesParts[1]),
        filter,
    };
};
